import {
  logDebug,
  logResults
} from "./chunk-KTPUX2XH.js";
import {
  DiscreetTypography
} from "./chunk-X3MX6SC5.js";

// lib/api/paths.ts
var relativeToRoot = (url, ...pathRaw) => {
  const path = pathRaw.map(
    (item) => item.startsWith("/") ? item.substring(1) : item
  );
  const pathStr = path.join("/").startsWith("/") ? path.join("/").substring(1) : path.join("/");
  if (url?.root?.url === void 0) {
    console.error("RootURL is not defined");
    const connectSlash2 = pathStr.startsWith("/") ? "" : "/";
    const result2 = `${connectSlash2}${path.join("/")}`;
    return result2;
  }
  const rootStr = url.root.url.toString();
  if (rootStr.includes("://")) {
    const baseUrl = rootStr.endsWith("/") ? rootStr : rootStr.substring(0, rootStr.lastIndexOf("/") + 1);
    const connectSlash2 = baseUrl.endsWith("/") || pathStr.startsWith("/") ? "" : "/";
    const result2 = `${baseUrl}${connectSlash2}${pathStr}`;
    return result2;
  }
  const connectSlash = rootStr.endsWith("/") || pathStr.startsWith("/") ? "" : "/";
  const result = `${rootStr}${connectSlash}${pathStr}`;
  return result;
};

// lib/api/version.ts
var buildInfoFile = "build.json";
var createAppBuildInfoUrl = (url, cacheBustId) => {
  const result = {
    build: {
      info: {
        url: relativeToRoot(url, buildInfoFile) + "?cacheBust=" + (cacheBustId ?? Date.now().toString())
      }
    }
  };
  return result;
};
var createApiBuildInfoUrl = (version, url) => {
  const result = {
    api: {
      info: {
        url: apiUrlEndpointFactory(version, url, "version").endpoint
      }
    }
  };
  return result;
};

// lib/api/api.ts
var kindId = (...segments) => segments.map((s) => `#${s}`).join("");
var parseJsonResponse = async (response) => {
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed === "") {
    const err = new Error(
      `Empty response body (HTTP ${response.status})`
    );
    err.code = response.status;
    throw err;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    const err = new Error(
      `Invalid JSON response (HTTP ${response.status}): ${trimmed.slice(0, 200)}`
    );
    err.code = response.status;
    throw err;
  }
};
var parseOptionalJsonResponse = async (response) => {
  const text = await response.text();
  const trimmed = text.trim();
  if (trimmed === "") {
    return null;
  }
  try {
    return JSON.parse(trimmed);
  } catch {
    const err = new Error(
      `Invalid JSON response (HTTP ${response.status}): ${trimmed.slice(0, 200)}`
    );
    err.code = response.status;
    throw err;
  }
};
var getAppVersion = async (root) => await fetch(createAppBuildInfoUrl(root).build.info.url).then(
  async (result) => await parseJsonResponse(result)
);
var getApiVersion = async (version, root, fetcher = fetch) => (
  // version.Handler() (lib/golang/api/version/handler.go) writes via kind.Unkinded's
  // OK().Json(ver) -- ver itself is the body, with no {item: ...} envelope the way a
  // kinds.<kind> CRUD result would have one. Parsing it as {item: ApiVersion} silently
  // produced item: undefined on every call (a genuine 200 that just never rendered),
  // masked until now by the request 403ing before this parse even mattered.
  await fetcher(createApiBuildInfoUrl(version, root).api.info.url).then(
    async (result) => await parseJsonResponse(result)
  )
);
var withVersion = (version, rootUrl, fetcher = fetch) => ({
  getAppVersion: async () => await getAppVersion(rootUrl),
  getApiVersion: async () => await getApiVersion(version, rootUrl, fetcher)
});
var _createApiUrl = (version, _root, ...relativePaths) => {
  const siteRoot = { root: { url: "/" } };
  const result = relativeToRoot(
    siteRoot,
    "api",
    version.apiVersion,
    ...relativePaths
  );
  logDebug("createApiUrl", version, siteRoot, relativePaths, "->", result);
  return result;
};
var withApi = (version, rootUrl) => ({
  create: (...relativePaths) => _createApiUrl(version, rootUrl, ...relativePaths)
});
var singleton = "@single";
var apiUrlCollectionFactory = (version, rootUrl, toId, ...relativePaths) => {
  return {
    collection: _createApiUrl(version, rootUrl, ...relativePaths),
    item: (data) => _createApiUrl(version, rootUrl, ...[...relativePaths, ...toId(data)])
  };
};
var apiUrlSingletonFactory = (version, rootUrl, ...relativePaths) => {
  return {
    collection: _createApiUrl(version, rootUrl, ...relativePaths),
    item: () => _createApiUrl(version, rootUrl, ...relativePaths, singleton)
  };
};
var apiUrlEndpointFactory = (version, rootUrl, ...relativePaths) => {
  return {
    endpoint: _createApiUrl(version, rootUrl, ...relativePaths)
  };
};
var apiUrlContainedCollectionFactory = (version, rootUrl, toCollectionPath, toItemPath) => {
  return {
    collection: (container) => _createApiUrl(version, rootUrl, ...toCollectionPath(container)),
    item: (data) => _createApiUrl(version, rootUrl, ...toItemPath(data))
  };
};

// lib/api/apiBuildNumber.tsx
import { useEffect, useState } from "react";
import { jsxs } from "react/jsx-runtime";
var ApiBuildNumberText = ({
  version: sdkVersion,
  rootUrl,
  fetcher
}) => {
  const [version, setVersion] = useState();
  useEffect(() => {
    const api = withVersion(sdkVersion, rootUrl, fetcher);
    void api.getApiVersion().then(logResults("api.build.number")).then((result) => {
      setVersion(result);
    });
  }, [rootUrl, sdkVersion, fetcher]);
  return version !== null && version !== void 0 && /* @__PURE__ */ jsxs(DiscreetTypography, { children: [
    "API ",
    version.build.number !== "" ? version.build.number : "[no build]"
  ] });
};

export {
  relativeToRoot,
  createAppBuildInfoUrl,
  createApiBuildInfoUrl,
  kindId,
  parseJsonResponse,
  parseOptionalJsonResponse,
  withVersion,
  withApi,
  apiUrlCollectionFactory,
  apiUrlSingletonFactory,
  apiUrlEndpointFactory,
  apiUrlContainedCollectionFactory,
  ApiBuildNumberText
};
